import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Award, Download, LogOut, BookOpen, PenLine, Sparkles, Linkedin, MessageCircle, Lock } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Progress } from "../components/ui/progress";
import { SubscriptionCard } from "../components/SubscriptionCard";

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [certs, setCerts] = useState([]);
  const [access, setAccess] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) { toast.error(t("security.mismatch")); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success(t("security.ok"));
      setPwd({ current: "", next: "", confirm: "" });
      setShowPwd(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const loadAll = useCallback(() => {
    api.get("/courses", { params: { level: 1 } }).then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/progress").then(({ data }) => setProgress(data)).catch(() => {});
    api.get("/exercises/reflections").then(({ data }) => setReflections(data)).catch(() => {});
    api.get("/exercises/quiz-lwa/result").then(({ data }) => data?.result && setQuiz(data.result)).catch(() => {});
    api.get("/certificates").then(({ data }) => setCerts(data)).catch(() => {});
    api.get("/access").then(({ data }) => setAccess(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (user) loadAll();
  }, [user, loading, navigate, loadAll]);

  if (loading || !user) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">{t("common.loading")}</div>;

  const coursePct = (c) => {
    const done = progress.filter((p) => p.course_id === c.course_id && p.completed).length;
    return Math.round((done / c.module_count) * 100);
  };
  const allDone = courses.length > 0 && courses.every((c) => coursePct(c) === 100);

  const getCert = async () => {
    try {
      const { data } = await api.post("/certificates/1");
      setCerts((prev) => (prev.some((x) => x.cert_id === data.cert_id) ? prev : [...prev, data]));
      toast.success("Certificat émis !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-6 py-16 relative z-10" data-testid="profile-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-full border-2 border-primary/40" />
          ) : (
            <span className="w-16 h-16 rounded-full bg-primary/15 border-2 border-primary/40 flex items-center justify-center font-serif-display text-2xl text-primary">
              {user.name?.[0]?.toUpperCase()}
            </span>
          )}
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif-display tracking-tight" data-testid="profile-name">{user.name}</h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            {access?.subscription_active && (
              <span className="inline-block mt-1 text-[10px] uppercase tracking-widest text-primary border border-primary/40 rounded-full px-3 py-0.5" data-testid="subscription-badge">
                {t("profile.subscription")}
              </span>
            )}
          </div>
        </div>
        <button onClick={async () => { await logout(); navigate("/"); }}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors duration-200"
          data-testid="logout-btn">
          <LogOut size={15} /> {t("nav.logout")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-14">
        {/* Progress */}
        <div className="lg:col-span-2 space-y-8">
          <section className="bg-card border border-primary/15 rounded-lg p-7" data-testid="progress-section">
            <h2 className="font-serif-display text-2xl flex items-center gap-3"><BookOpen size={19} strokeWidth={1.5} className="text-primary" /> {t("profile.progress")}</h2>
            <div className="mt-6 space-y-5">
              {courses.map((c) => (
                <Link to={`/cours/${c.course_id}`} key={c.course_id} className="block group" data-testid={`profile-course-${c.course_id}`}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-foreground/80 group-hover:text-primary transition-colors duration-200">{c.title}</span>
                    <span className="text-muted-foreground">{coursePct(c)}%</span>
                  </div>
                  <Progress value={coursePct(c)} className="h-1.5" />
                </Link>
              ))}
              {courses.length === 0 && <p className="text-sm text-muted-foreground">{t("profile.noProgress")}</p>}
            </div>
          </section>

          <section className="bg-card border border-primary/15 rounded-lg p-7" data-testid="reflections-section">
            <h2 className="font-serif-display text-2xl flex items-center gap-3"><PenLine size={19} strokeWidth={1.5} className="text-primary" /> {t("profile.reflections")}</h2>
            <div className="mt-5 space-y-4">
              {reflections.map((r) => (
                <div key={r.exercise_id} className="border-l-2 border-primary/30 pl-4" data-testid={`reflection-${r.exercise_id}`}>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.course_id} · {r.exercise_id}</p>
                  <p className="text-sm text-foreground/75 mt-1 leading-relaxed line-clamp-3">{r.content}</p>
                </div>
              ))}
              {reflections.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
            </div>
          </section>
        </div>

        {/* Right col */}
        <div className="space-y-8">
          <SubscriptionCard />
          <section className="bg-card border border-primary/15 rounded-lg p-7" data-testid="certificates-section">
            <h2 className="font-serif-display text-2xl flex items-center gap-3"><Award size={19} strokeWidth={1.5} className="text-primary" /> {t("profile.certificates")}</h2>
            <div className="mt-5 space-y-4">
              {certs.map((c) => (
                <div key={c.cert_id} className="border border-primary/25 rounded-lg p-4" data-testid={`certificate-${c.cert_id}`}>
                  <p className="text-sm font-medium">{c.level_name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono mt-1">{c.cert_id}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-3">
                    <a href={`${API}/certificates/${c.cert_id}/pdf`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs text-primary hover:underline underline-offset-4"
                      data-testid={`cert-download-${c.cert_id}`}>
                      <Download size={13} /> {t("profile.download")}
                    </a>
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/verify/${c.cert_id}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline underline-offset-4"
                      data-testid={`cert-share-linkedin-${c.cert_id}`}>
                      <Linkedin size={13} /> LinkedIn
                    </a>
                    <a href={`https://wa.me/?text=${encodeURIComponent(`J'ai obtenu mon certificat « ${c.level_name} » — Chimen Nanm, Fondation Sèlide Kouzen-Kouzin. Vérifiez-le ici : ${window.location.origin}/verify/${c.cert_id}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline underline-offset-4"
                      data-testid={`cert-share-whatsapp-${c.cert_id}`}>
                      <MessageCircle size={13} /> WhatsApp
                    </a>
                  </div>
                </div>
              ))}
              {certs.length === 0 && (
                <button onClick={getCert} disabled={!allDone}
                  className="w-full py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="get-certificate-btn">
                  {t("profile.getCert")} {!allDone && "(Niveau 1 : 100 % requis)"}
                </button>
              )}
            </div>
          </section>

          <section className="bg-card border border-primary/15 rounded-lg p-7" data-testid="security-section">
            <h2 className="font-serif-display text-2xl flex items-center gap-3"><Lock size={19} strokeWidth={1.5} className="text-primary" /> {t("security.title")}</h2>
            {!showPwd ? (
              <button onClick={() => setShowPwd(true)}
                className="mt-5 w-full py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors duration-200"
                data-testid="change-password-btn">
                {t("security.changePassword")}
              </button>
            ) : (
              <form onSubmit={changePassword} className="mt-5 space-y-3" data-testid="change-password-form">
                <input type="password" required value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                  placeholder={t("security.current")}
                  className="w-full px-4 py-2.5 rounded-lg border border-primary/25 bg-background text-sm focus:outline-none focus:border-primary"
                  data-testid="current-password-input" />
                <input type="password" required minLength={8} value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                  placeholder={t("security.newPass")}
                  className="w-full px-4 py-2.5 rounded-lg border border-primary/25 bg-background text-sm focus:outline-none focus:border-primary"
                  data-testid="new-password-input" />
                <input type="password" required minLength={8} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                  placeholder={t("security.confirm")}
                  className="w-full px-4 py-2.5 rounded-lg border border-primary/25 bg-background text-sm focus:outline-none focus:border-primary"
                  data-testid="confirm-password-input" />
                <div className="flex gap-3">
                  <button type="submit" className="flex-1 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200" data-testid="submit-password-btn">
                    {t("security.submit")}
                  </button>
                  <button type="button" onClick={() => setShowPwd(false)} className="px-5 py-2.5 rounded-full border border-primary/25 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="cancel-password-btn">
                    ✕
                  </button>
                </div>
              </form>
            )}
          </section>

          {quiz && (
            <section className="bg-primary/5 border border-primary/30 rounded-lg p-7" data-testid="quiz-result-section">
              <h2 className="font-serif-display text-2xl flex items-center gap-3"><Sparkles size={19} strokeWidth={1.5} className="text-primary" /> {t("profile.quizResult")}</h2>
              <p className="font-serif-display text-3xl text-primary mt-4">{quiz.name}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{quiz.title}</p>
            </section>
          )}
        </div>
      </div>
    </main>
  );
}
